/**
 * Edit-time workflow graph validation — extracted from WorkflowEditor
 * so the pure logic is unit-testable without dragging in ReactFlow,
 * EventStream, Monaco, etc. (same pattern as ``outputHandles.ts``).
 *
 * The checks are conservative: they only flag cases that are
 * *certainly* wrong, never stylistic preferences. ``error`` blocks
 * save; ``warning`` is informational. ``nodeIds`` / ``edgeIds`` let the
 * UI highlight the offending elements.
 *
 * Typed against minimal structural shapes (``ValidatableNode`` /
 * ``ValidatableEdge``) rather than ReactFlow's ``Node<NodeData>`` /
 * ``Edge`` so the module has zero runtime React dependency. The editor's
 * concrete types are structurally compatible — callers pass them
 * unchanged.
 */
import type { NodeKind } from "@/lib/api-workflows";

import { isEntrypointKind, wouldCreateCycle } from "./nodeGraph";
import { outputHandlesFor } from "./outputHandles";
import { isSubflowLinkEdge } from "./subflowOrchestration";


export interface GraphIssue {
  severity: "error" | "warning";
  message: string;
  nodeIds: string[];
  edgeIds: string[];
}

/** Minimal node shape validateGraph needs. The editor's
 *  ``Node<NodeData>`` satisfies this structurally. */
export interface ValidatableNode {
  id: string;
  data: {
    kind: NodeKind;
    label?: string | null;
    config?: Record<string, unknown>;
    spec?: { category?: string } | undefined;
  };
}

/** Minimal edge shape. ReactFlow's ``Edge`` satisfies this. */
export interface ValidatableEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
}


/** Validate the graph at edit time so the user gets immediate feedback
 *  rather than a backend 500 at save (or — worse — a runtime failure
 *  deep in execution). */
export function validateGraph(
  nodes: ValidatableNode[],
  edges: ValidatableEdge[],
): GraphIssue[] {
  const issues: GraphIssue[] = [];
  const nodeById = new Map(nodes.map((n) => [n.id, n] as const));

  // ADR-0008 PR3b3c — drop the virtual sub-flow link edges (sink
  // branch handle → delivery_trigger). They are a pure visual
  // connector, not a real WorkflowEdge: the link lives in
  // SinkBranch.linked_workflow_id. All structural rules below would
  // false-flag them (sink-has-no-output, source-has-no-input,
  // orphan-handle, etc.) — easier to filter once at the top.
  const realEdges = edges.filter((e) => !isSubflowLinkEdge(e));

  // 1) self-loops
  for (const e of realEdges) {
    if (e.source === e.target) {
      issues.push({
        severity: "error",
        message: `Edge ${e.id}: a node cannot connect to itself`,
        nodeIds: [e.source],
        edgeIds: [e.id],
      });
    }
  }

  // 2) duplicate edges (same source+source_handle+target)
  const seen = new Map<string, string[]>();
  for (const e of realEdges) {
    const key = `${e.source}::${e.sourceHandle ?? ""}::${e.target}`;
    const prev = seen.get(key) ?? [];
    prev.push(e.id);
    seen.set(key, prev);
  }
  for (const [, ids] of seen) {
    if (ids.length > 1) {
      issues.push({
        severity: "error",
        message: `Duplicate edges between the same nodes & handle (${ids.length}×) — keep one`,
        nodeIds: [],
        edgeIds: ids,
      });
    }
  }

  // 3) edges out of a sink / into a source — kinds that don't accept
  //    them. Sinks have no outputs; sources have no inputs.
  for (const e of realEdges) {
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (src?.data.spec?.category === "sink") {
      issues.push({
        severity: "error",
        message: `Edge ${e.id}: sink "${src.data.label ?? src.id}" has no output`,
        nodeIds: [src.id],
        edgeIds: [e.id],
      });
    }
    // Entry points (triggers + read_canonical) have no input — a run
    // starts there, nothing may connect INTO them.
    if (tgt && isEntrypointKind(tgt.data.kind, tgt.data.spec?.category)) {
      issues.push({
        severity: "error",
        message: `Edge ${e.id}: "${tgt.data.label ?? tgt.id}" is a trigger — it has no input`,
        nodeIds: [tgt.id],
        edgeIds: [e.id],
      });
    }
  }

  // 3b) cycles — the graph must be a DAG EXCEPT for the legal loop cycle.
  //     A `loop` (Split-In-Batches) node's body edges BACK into it (the
  //     intended graphical cycle). We must exempt the WHOLE loop cycle, not
  //     just the back-edge: the forward edges of the body (loop→…→back) are
  //     ALSO part of that cycle, so exempting only the back-edge still
  //     false-flags a forward body edge (the "Edge e3 creates a cycle" bug).
  //     Mirror the backend `_assert_acyclic`: STRIP every back-edge into a
  //     loop node FIRST, then run cycle detection on the reduced graph — a
  //     cycle that only existed because of the loop back-edge disappears,
  //     while a genuinely illegal cycle still surfaces.
  const acyclicEdges = realEdges.filter(
    (e) => nodeById.get(e.target)?.data.kind !== "loop",
  );
  for (const e of acyclicEdges) {
    if (e.source === e.target) continue; // self-loops are rule 1's job
    const others = acyclicEdges.filter((x) => x !== e);
    if (wouldCreateCycle(others, e.source, e.target)) {
      issues.push({
        severity: "error",
        message: `Edge ${e.id}: creates a cycle — workflows must be acyclic (DAG)`,
        nodeIds: [e.source, e.target],
        edgeIds: [e.id],
      });
    }
  }

  // 4) orphan source_handle: edge points to a handle id that no longer
  //    exists on the source node (case removed on a switch, etc.).
  for (const e of realEdges) {
    if (!e.sourceHandle) continue;
    const src = nodeById.get(e.source);
    if (!src) continue;
    const handles = outputHandlesFor(src.data.kind, src.data.config ?? {});
    if (handles.length === 0) continue;  // node has no named handles → ignore
    if (!handles.some((h) => h.id === e.sourceHandle)) {
      issues.push({
        severity: "error",
        message: `Edge ${e.id}: handle "${e.sourceHandle}" no longer exists on ${src.data.label ?? src.id}`,
        nodeIds: [src.id],
        edgeIds: [e.id],
      });
    }
  }

  // 5) routing nodes (branch/switch) missing edges for some of their
  //    declared handles. Warning rather than error: an unused handle
  //    is technically valid (drops the message) — but probably a bug.
  //
  //    Sink handles are EXEMPT: the branches surface optional outcome
  //    hooks (succeeded / dead_letter / custom). An operator may
  //    legitimately leave a branch un-wired — the dispatcher just
  //    won't trigger a sub-flow there. Surfacing it as a warning
  //    creates noise on every sink without custom routing.
  for (const n of nodes) {
    if (n.data.spec?.category === "sink") continue;
    const handles = outputHandlesFor(n.data.kind, n.data.config ?? {});
    if (handles.length === 0) continue;
    const usedHandles = new Set(
      realEdges.filter((e) => e.source === n.id).map((e) => e.sourceHandle ?? null),
    );
    const missing = handles.filter((h) => !usedHandles.has(h.id));
    if (missing.length > 0) {
      issues.push({
        severity: "warning",
        message: `${n.data.label ?? n.id}: ${missing.length} handle${
          missing.length > 1 ? "s have" : " has"
        } no outgoing edge (${missing.map((h) => h.id).join(", ")})`,
        nodeIds: [n.id],
        edgeIds: [],
      });
    }
  }

  // 6) sources with no outgoing edge — they'd never trigger anything.
  //    EXEMPT: ``delivery_trigger`` sources living inside a sub-flow
  //    region. Those are auto-created with just the trigger node and
  //    the operator extends downstream from there; warning them on
  //    every fresh sub-flow would be noise.
  for (const n of nodes) {
    if (n.data.spec?.category !== "source") continue;
    if (n.data.kind === "delivery_trigger") continue;
    const hasOut = realEdges.some((e) => e.source === n.id);
    if (!hasOut) {
      issues.push({
        severity: "warning",
        message: `Source "${n.data.label ?? n.id}" has no outgoing edge — it'll never drive anything`,
        nodeIds: [n.id],
        edgeIds: [],
      });
    }
  }

  return issues;
}
